export interface CodePart {
  kind: 'text' | 'code';
  value: string;
  /** Where the part starts in the source text — a stable identity for it, unlike its position. */
  offset: number;
}

/**
 * The tokens in a run's free text that are machine names rather than words: the tools the agent
 * called, the fields it read, the argument it passed, and the instant a figure was computed at.
 *
 * Nothing in the contract marks them — `evidence.sources[].statement` is a plain string the model
 * wrote — so they are recognised by shape. Deliberately narrow: a token has to be something no
 * English sentence produces by accident.
 *
 * Bare numbers are not tokens. An id and a count are written the same way ("item 118834", "1,363
 * items"), so marking one would mark the other, and a sentence where half the numbers are boxed
 * reads as a sentence with a rendering bug.
 */
const CODE_TOKEN = new RegExp(
  [
    // An instant, as the store reports it: 2026-09-04T03:00:21Z.
    '\\d{4}-\\d{2}-\\d{2}T\\d{2}:\\d{2}:\\d{2}(?:\\.\\d+)?Z?',
    // A snake_case name — get_objective_health — with the argument it was called with when it has
    // one, so `serves_objective_ids:[68]` stays a single token rather than a name and some debris.
    '\\b[a-z][a-z0-9]*(?:_[a-z0-9]+)+(?::\\[[^\\]\\s]*\\]|:[A-Za-z0-9_.-]+)?',
    // A camelCase field name — linkedItemCount.
    '\\b[a-z][a-z0-9]*(?:[A-Z][a-z0-9]+)+',
  ].join('|'),
  'g',
);

/**
 * Splits text into the words a reader reads and the tokens they scan, in order. Text runs are
 * returned as they were: whoever renders them decides what else to do with them.
 */
export const splitCodeTokens = (text: string): CodePart[] => {
  const parts: CodePart[] = [];
  let cursor = 0;

  for (const match of text.matchAll(CODE_TOKEN)) {
    if (match.index > cursor) {
      parts.push({ kind: 'text', value: text.slice(cursor, match.index), offset: cursor });
    }
    parts.push({ kind: 'code', value: match[0], offset: match.index });
    cursor = match.index + match[0].length;
  }

  if (cursor < text.length) {
    parts.push({ kind: 'text', value: text.slice(cursor), offset: cursor });
  }

  return parts;
};
